import type { GridSortModel } from "@mui/x-data-grid";
import type { DataGridProProps } from "@mui/x-data-grid-pro";

import { getMarketsDataGridStyles } from "~/feature/event/components/MarketsDataGrid/MarketsDataGrid.styles";
import { MarketsDataGridField } from "~/feature/event/components/MarketsDataGrid/types";

export const DEFAULT_MARKETS_SORT_MODEL: GridSortModel = [
  { field: MarketsDataGridField.Chance, sort: "desc" },
];

export const INITIAL_MARKETS_LIMIT = 25;
export const MARKET_ROW_HEIGHT = 52;
export const GROUPED_ROW_HEIGHT = 30;

export const labelByMarketsDataGridField: Record<MarketsDataGridField, string> =
  {
    [MarketsDataGridField.Market]: "Outcome",
    [MarketsDataGridField.Chance]: "Chance %",
    [MarketsDataGridField.Price]: "Price",
    [MarketsDataGridField.Spread]: "Spread",
    [MarketsDataGridField.Liquidity]: "Liquidity",
    [MarketsDataGridField.Volume]: "Volume",
    [MarketsDataGridField.H24]: "24h",
    [MarketsDataGridField.D7]: "7d",
    [MarketsDataGridField.OpenInterest]: "Open Interest",
    [MarketsDataGridField.EndsIn]: "Ends in",
    [MarketsDataGridField.Status]: "Result",
    [MarketsDataGridField.Trade]: "Trade",
  };

export const tooltipByMarketsDataGridField: Record<
  MarketsDataGridField,
  string | null
> = {
  [MarketsDataGridField.Market]:
    "Ranked by probability and grouped across platforms. Toggle to ungroup.",
  [MarketsDataGridField.Chance]:
    "Chance is intended to represent the market’s current implied probability.\n\nRed and green indicators show the 24-hour change.\n\nPH Consensus is the average across platforms weighted by each market's trading volume, so thinly traded markets can't skew it. Markets with a bid/ask gap wider than 10¢ are left out.\n\nNote: When combining two outcomes, the total probability may not always equal 100%.",
  [MarketsDataGridField.Price]:
    "The current price to buy a “Yes” or “No” share.",
  [MarketsDataGridField.Spread]:
    "Spread is the difference between the best ask and best bid price. Available only for active events. Smaller spreads generally indicate higher liquidity.\n",
  [MarketsDataGridField.Liquidity]:
    "The amount of money available to trade immediately without significantly changing the price.",
  [MarketsDataGridField.Volume]:
    "The total dollar amount of all trades made on this specific outcome since the market opened.",
  [MarketsDataGridField.H24]:
    "Change in traded volume over the last 24 hours.",
  [MarketsDataGridField.D7]:
    "Change in traded volume over the last seven days. N/A on markets listed more recently than that.",
  [MarketsDataGridField.OpenInterest]:
    "The total value of all active contracts currently held by traders that have not yet been settled.",
  [MarketsDataGridField.EndsIn]:
    "Indicates when the event or market is scheduled to conclude.\nAfter the end date passes, the market may remain active until the final outcome is verified and resolved. In some cases, a dispute may delay resolution.",
  [MarketsDataGridField.Status]:
    "The market eventually resolves to Yes or No, in which case trading is closed.\nIf the market is Disputed, a dispute is pending and the final outcome has not yet been confirmed. Trading may still be possible during this period. Trade with caution.",
  [MarketsDataGridField.Trade]: null,
};

export const marketsDataGridSettings: Partial<DataGridProProps> = {
  sx: getMarketsDataGridStyles,
  localeText: {
    footerTotalRows: "Total markets:",
    noRowsLabel:
      "Nothing found. Try adjusting your filters or search criteria.",
  },
};
